import React, { useEffect, useMemo, useRef, useState } from "react";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import "./styles/MainCalendar.css";

const normalizeRange = (start, end) => {
  if (!start || !end) return { start, end };
  return start <= end ? { start, end } : { start: end, end: start };
};

const toDate = (value) => {
  if (!value) return null;
  const parsed = value instanceof Date ? new Date(value) : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDisplayDate = (date, locale = "es-CL") =>
  date.toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

function Calendar({ state, setParentState, translate, variant = "default", hideToolbar = false }) {
  const t = (key, fallback) => {
    const value = typeof translate === "function" ? translate(key) : key;
    return value && value !== key ? value : fallback;
  };

  const today = useMemo(() => new Date(), []);
  const monthPickerRef = useRef(null);
  const yearPickerRef = useRef(null);

  const [displayedMonth, setDisplayedMonth] = useState(today.getMonth());
  const [displayedYear, setDisplayedYear] = useState(today.getFullYear());
  const [selectedRange, setSelectedRange] = useState({ start: today, end: today });
  const [isSelecting, setIsSelecting] = useState(false);
  const [activeQuickRange, setActiveQuickRange] = useState(null);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [daysInput, setDaysInput] = useState("");

  const months = [
    t("month_short_jan", "Ene"),
    t("month_short_feb", "Feb"),
    t("month_short_mar", "Mar"),
    t("month_short_apr", "Abr"),
    t("month_short_may", "May"),
    t("month_short_jun", "Jun"),
    t("month_short_jul", "Jul"),
    t("month_short_aug", "Ago"),
    t("month_short_sep", "Sep"),
    t("month_short_oct", "Oct"),
    t("month_short_nov", "Nov"),
    t("month_short_dec", "Dic"),
  ];

  const weekdays = [
    t("weekday_mon", "Lun"),
    t("weekday_tue", "Mar"),
    t("weekday_wed", "Mie"),
    t("weekday_thu", "Jue"),
    t("weekday_fri", "Vie"),
    t("weekday_sat", "Sab"),
    t("weekday_sun", "Dom"),
  ];

  const years = useMemo(
    () => Array.from({ length: new Date().getFullYear() - 2020 + 2 }, (_, index) => 2020 + index),
    []
  );

  useEffect(() => {
    const start = toDate(state?.[0]?.startDate);
    const end = toDate(state?.[0]?.endDate);

    if (!start || !end) return;

    const normalized = normalizeRange(start, end);
    setSelectedRange(normalized);
    setDisplayedMonth(normalized.end.getMonth());
    setDisplayedYear(normalized.end.getFullYear());
  }, [state]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMonthPicker && monthPickerRef.current && !monthPickerRef.current.contains(event.target)) {
        setShowMonthPicker(false);
      }
      if (showYearPicker && yearPickerRef.current && !yearPickerRef.current.contains(event.target)) {
        setShowYearPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMonthPicker, showYearPicker]);

  const syncRange = (startValue, endValue, quickKey = null) => {
    const { start, end } = normalizeRange(startValue, endValue);
    setSelectedRange({ start, end });
    setActiveQuickRange(quickKey);
    setDisplayedMonth(end.getMonth());
    setDisplayedYear(end.getFullYear());
    setParentState([
      {
        startDate: start,
        endDate: end,
        key: "selection",
      },
    ]);
  };

  const getCalendarDays = (month, year) => {
    const firstDay = new Date(year, month, 1);
    const startIndex = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    const lastDay = new Date(year, month + 1, 0);
    const previousMonthLastDay = new Date(year, month, 0).getDate();
    const days = [];
    let nextMonthDay = 1;

    for (let index = startIndex; index > 0; index -= 1) {
      days.push({
        date: new Date(year, month - 1, previousMonthLastDay - index + 1),
        currentMonth: false,
      });
    }

    for (let day = 1; day <= lastDay.getDate(); day += 1) {
      days.push({
        date: new Date(year, month, day),
        currentMonth: true,
      });
    }

    while (days.length % 7) {
      days.push({
        date: new Date(year, month + 1, nextMonthDay),
        currentMonth: false,
      });
      nextMonthDay += 1;
    }

    while (days.length < 42) {
      days.push({
        date: new Date(year, month + 1, nextMonthDay),
        currentMonth: false,
      });
      nextMonthDay += 1;
    }

    return Array.from({ length: days.length / 7 }, (_, index) =>
      days.slice(index * 7, index * 7 + 7)
    );
  };

  const handleQuickRange = (key, start, end) => {
    setDaysInput("");
    setIsSelecting(false);
    syncRange(start, end, key);
  };

  const handleSelectThisWeek = () => {
    const dayIndex = today.getDay() === 0 ? 6 : today.getDay() - 1;
    const start = new Date(today);
    start.setDate(today.getDate() - dayIndex);
    handleQuickRange("this_week", start, today);
  };

  const handleSelectToday = () => handleQuickRange("today", today, today);

  const handleSelectYesterday = () => {
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    handleQuickRange("yesterday", yesterday, yesterday);
  };

  const handleSelectLastWeek = () => {
    const dayIndex = today.getDay() === 0 ? 6 : today.getDay() - 1;
    const startOfThisWeek = new Date(today);
    startOfThisWeek.setDate(today.getDate() - dayIndex);
    const start = new Date(startOfThisWeek);
    start.setDate(startOfThisWeek.getDate() - 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    handleQuickRange("last_week", start, end);
  };

  const handleSelectThisMonth = () => {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    handleQuickRange("this_month", start, today);
  };

  const handleSelectLastMonth = () => {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    handleQuickRange("last_month", start, end);
  };

  const handleSelectRemainingYear = () => {
    const end = new Date(today.getFullYear(), 11, 31);
    handleQuickRange("this_year_from_today", today, end);
  };

  const handleSelectFullYear = () => {
    const start = new Date(today.getFullYear(), 0, 1);
    const end = new Date(today.getFullYear(), 11, 31);
    handleQuickRange("full_year", start, end);
  };

  const handleDaysUntilToday = () => {
    const amount = Number(daysInput);
    if (!Number.isFinite(amount) || amount <= 0) return;
    const start = new Date(today);
    start.setDate(today.getDate() - amount + 1);
    handleQuickRange("days_until_today", start, today);
  };

  const handleDaysFromToday = () => {
    const amount = Number(daysInput);
    if (!Number.isFinite(amount) || amount <= 0) return;
    const end = new Date(today);
    end.setDate(today.getDate() + amount - 1);
    handleQuickRange("days_from_today", today, end);
  };

  const handleDayClick = (day) => {
    setActiveQuickRange(null);

    if (!selectedRange.start || !isSelecting) {
      setSelectedRange({ start: day, end: day });
      setIsSelecting(true);
      return;
    }

    setIsSelecting(false);
    syncRange(selectedRange.start, day, null);
  };

  const handleMouseEnter = (day) => {
    if (isSelecting && selectedRange.start) {
      setSelectedRange((previous) => ({ ...previous, end: day }));
    }
  };

  const isInSelectedRange = (day) => {
    if (!selectedRange.start || !selectedRange.end) return false;
    const { start, end } = normalizeRange(selectedRange.start, selectedRange.end);
    const time = day.getTime();
    return time >= start.getTime() && time <= end.getTime();
  };

  const isRangeEdge = (day, edge) => {
    const target = selectedRange[edge];
    return target && day.toDateString() === target.toDateString();
  };

  const navigateMonth = (direction) => {
    let nextMonth = displayedMonth + direction;
    let nextYear = displayedYear;

    if (nextMonth < 0) {
      nextMonth = 11;
      nextYear -= 1;
    }

    if (nextMonth > 11) {
      nextMonth = 0;
      nextYear += 1;
    }

    setDisplayedMonth(nextMonth);
    setDisplayedYear(nextYear);
  };

  const rangeSummary = useMemo(() => {
    const { start, end } = normalizeRange(selectedRange.start, selectedRange.end);
    if (!start || !end) return "";
    if (start.toDateString() === end.toDateString()) return formatDisplayDate(start);
    return `${formatDisplayDate(start)} - ${formatDisplayDate(end)}`;
  }, [selectedRange.end, selectedRange.start]);

  const rangeDays = useMemo(() => {
    const { start, end } = normalizeRange(selectedRange.start, selectedRange.end);
    if (!start || !end) return 0;
    return Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
  }, [selectedRange.end, selectedRange.start]);

  const renderCalendar = (month, year, label) => {
    const weeks = getCalendarDays(month, year);

    return (
      <section className="calendar-card">
        <div className="calendar-card-header">
          <span className="calendar-card-label">{label}</span>
          <span className="calendar-card-title">
            {months[month]} {year}
          </span>
        </div>

        <table className="calendar-table">
          <thead>
            <tr>
              {weekdays.map((weekday) => (
                <th key={weekday}>{weekday}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, weekIndex) => (
              <tr key={`${month}-${year}-${weekIndex}`}>
                {week.map((cell) => {
                  const day = cell.date;
                  const isToday = day.toDateString() === today.toDateString();
                  const isSelected = isInSelectedRange(day);
                  const isStart = isRangeEdge(day, "start");
                  const isEnd = isRangeEdge(day, "end");

                  return (
                    <td key={`${month}-${year}-${day.toISOString()}`}>
                      <button
                        type="button"
                        className={[
                          "calendar-day",
                          cell.currentMonth ? "" : "not-current",
                          isSelected ? "selected" : "",
                          isStart ? "range-start" : "",
                          isEnd ? "range-end" : "",
                          isToday ? "today" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => handleDayClick(day)}
                        onMouseEnter={() => handleMouseEnter(day)}
                      >
                        <span>{day.getDate()}</span>
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    );
  };

  const previousMonth = displayedMonth === 0 ? 11 : displayedMonth - 1;
  const previousYear = displayedMonth === 0 ? displayedYear - 1 : displayedYear;
  const isConfigVariant = variant === "config";

  return (
    <div className="calendar-component">
      {!hideToolbar && <div className="calendar-toolbar">
        <div className="calendar-toolbar-copy">
          <span className="calendar-toolbar-kicker">{t("temporal_range", "Rango temporal")}</span>
          <h3>{t("calendar", "Calendario")}</h3>
          <p>{rangeSummary}</p>
        </div>

        <div className="calendar-range-summary">
          <span>{t("days", "Días")}</span>
          <strong>{rangeDays}</strong>
        </div>
      </div>}

      <div className="calendar-quick-actions">
        {isConfigVariant ? (
          <>
            <button
              type="button"
              className={activeQuickRange === "this_week" ? "active" : ""}
              onClick={handleSelectThisWeek}
            >
              {t("this_week", "Esta semana")}
            </button>
            <button
              type="button"
              className={activeQuickRange === "this_month" ? "active" : ""}
              onClick={handleSelectThisMonth}
            >
              {t("this_month", "Este mes")}
            </button>
            <button
              type="button"
              className={activeQuickRange === "this_year_from_today" ? "active" : ""}
              onClick={handleSelectRemainingYear}
            >
              {t("this_year_from_today", "Este año desde hoy")}
            </button>
            <button
              type="button"
              className={activeQuickRange === "full_year" ? "active" : ""}
              onClick={handleSelectFullYear}
            >
              {t("full_year", "Este año completo")}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={activeQuickRange === "today" ? "active" : ""}
              onClick={handleSelectToday}
            >
              {t("today", "Hoy")}
            </button>
            <button
              type="button"
              className={activeQuickRange === "yesterday" ? "active" : ""}
              onClick={handleSelectYesterday}
            >
              {t("yesterday", "Ayer")}
            </button>
            <button
              type="button"
              className={activeQuickRange === "this_week" ? "active" : ""}
              onClick={handleSelectThisWeek}
            >
              {t("this_week", "Esta semana")}
            </button>
            <button
              type="button"
              className={activeQuickRange === "last_week" ? "active" : ""}
              onClick={handleSelectLastWeek}
            >
              {t("last_week", "Semana pasada")}
            </button>
            <button
              type="button"
              className={activeQuickRange === "this_month" ? "active" : ""}
              onClick={handleSelectThisMonth}
            >
              {t("this_month", "Este mes")}
            </button>
            <button
              type="button"
              className={activeQuickRange === "last_month" ? "active" : ""}
              onClick={handleSelectLastMonth}
            >
              {t("last_month", "Mes pasado")}
            </button>
          </>
        )}

        <div className="calendar-days-input">
          <span>
            {isConfigVariant
              ? t("days_from_today", "Dias desde hoy")
              : t("days_until_today", "Días hasta hoy")}
          </span>
          <div className="calendar-days-input-group">
            <input
              type="number"
              min="1"
              value={daysInput}
              onChange={(event) => setDaysInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  if (isConfigVariant) {
                    handleDaysFromToday();
                  } else {
                    handleDaysUntilToday();
                  }
                }
              }}
              placeholder="30"
            />
            <button
              type="button"
              onClick={isConfigVariant ? handleDaysFromToday : handleDaysUntilToday}
            >
              {t("accept_button", "Aplicar")}
            </button>
          </div>
        </div>
      </div>

      <div className="calendar-navigation">
        <button type="button" className="calendar-nav-button" onClick={() => navigateMonth(-1)}>
          <KeyboardArrowLeftIcon />
        </button>

        <div className="calendar-navigation-center">
          <div className="calendar-picker" ref={monthPickerRef}>
            <button
              type="button"
              className="calendar-picker-trigger"
              onClick={() => {
                setShowMonthPicker((previous) => !previous);
                setShowYearPicker(false);
              }}
            >
              {months[displayedMonth]}
            </button>
            {showMonthPicker && (
              <div className="calendar-picker-menu">
                {months.map((monthLabel, index) => (
                  <button
                    type="button"
                    key={monthLabel}
                    className={index === displayedMonth ? "active" : ""}
                    onClick={() => {
                      setDisplayedMonth(index);
                      setShowMonthPicker(false);
                    }}
                  >
                    {monthLabel}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="calendar-picker" ref={yearPickerRef}>
            <button
              type="button"
              className="calendar-picker-trigger"
              onClick={() => {
                setShowYearPicker((previous) => !previous);
                setShowMonthPicker(false);
              }}
            >
              {displayedYear}
            </button>
            {showYearPicker && (
              <div className="calendar-picker-menu calendar-picker-menu-years">
                {years.map((year) => (
                  <button
                    type="button"
                    key={year}
                    className={year === displayedYear ? "active" : ""}
                    onClick={() => {
                      setDisplayedYear(year);
                      setShowYearPicker(false);
                    }}
                  >
                    {year}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <button type="button" className="calendar-nav-button" onClick={() => navigateMonth(1)}>
          <KeyboardArrowRightIcon />
        </button>
      </div>

      <div className="calendars-container">
        {renderCalendar(previousMonth, previousYear, t("previous_period", "Previous"))}
        {renderCalendar(displayedMonth, displayedYear, t("current_period", "Current"))}
      </div>
    </div>
  );
}

function MainCalendar({ state, setState, translate, variant = "default", hideToolbar = false }) {
  return (
    <div className="calendar-container">
      <Calendar
        state={state}
        setParentState={setState}
        translate={translate}
        variant={variant}
        hideToolbar={hideToolbar}
      />
    </div>
  );
}

export default MainCalendar;
